/**
 * « Mon entreprise » (`/entreprise`, refonte v4) : un accueil en six groupes et
 * une page par rubrique, chacune ouverte par la capacité qui la règle (l'API
 * revérifie). Les anciennes adresses `/parametres…` (notifications, e-mails,
 * signets, retours de connexion) mènent à leur nouvelle page. L'e-mail et le
 * mot de passe de son propre compte vivent dans « Mon compte ».
 */
import { lazy, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { AppRoute, RouteGate } from '../../app/route-types.js';
import { GDPR_REQUESTS_PATH } from '../../core/notification-target.js';
import { legacySettingsPath } from './legacy-paths.js';
import { ENTREPRISE_ANY, ENTREPRISE_SECTIONS } from './sections/registry.js';

const EntreprisePage = lazy(() => import('./EntreprisePage.js').then((m) => ({ default: m.EntreprisePage })));
const IdentityPage = lazy(() => import('./sections/IdentityPage.js').then((m) => ({ default: m.IdentityPage })));
const IdentityEditPage = lazy(() => import('./sections/IdentityEditPage.js').then((m) => ({ default: m.IdentityEditPage })));
const BrandingPage = lazy(() => import('./sections/wrapped.js').then((m) => ({ default: m.BrandingPage })));
const MembersPage = lazy(() => import('./sections/MembersPage.js').then((m) => ({ default: m.MembersPage })));
const MemberPage = lazy(() => import('./sections/MemberPage.js').then((m) => ({ default: m.MemberPage })));
const InviteMemberPage = lazy(() => import('./sections/InviteMemberPage.js').then((m) => ({ default: m.InviteMemberPage })));
const RolesPage = lazy(() => import('./sections/wrapped.js').then((m) => ({ default: m.RolesPage })));
const accountants = () => import('./sections/AccountantPages.js');
const AccountantsPage = lazy(() => accountants().then((m) => ({ default: m.AccountantsPage })));
const InviteAccountantPage = lazy(() => accountants().then((m) => ({ default: m.InviteAccountantPage })));
const AccountantPage = lazy(() => accountants().then((m) => ({ default: m.AccountantPage })));
const ConvertAccountantPage = lazy(() => accountants().then((m) => ({ default: m.ConvertAccountantPage })));
const MessagingSettingsPage = lazy(() => import('./sections/messaging-settings.js').then((m) => ({ default: m.MessagingSettingsPage })));
const AuditLogPage = lazy(() => import('./sections/AuditLogPage.js').then((m) => ({ default: m.AuditLogPage })));
const RolePage = lazy(() => import('./RolePage.js').then((m) => ({ default: m.RolePage })));
const TradesPage = lazy(() => import('./sections/activity-company.js').then((m) => ({ default: m.TradesPage })));
const CustomersPage = lazy(() => import('./sections/activity-company.js').then((m) => ({ default: m.CustomersPage })));
const FluidsSettingsPage = lazy(() => import('./sections/activity-company.js').then((m) => ({ default: m.FluidsSettingsPage })));
const ReservedPartsPage = lazy(() => import('./sections/activity-company.js').then((m) => ({ default: m.ReservedPartsPage })));
const WorkingHoursPage = lazy(() => import('./sections/activity-field.js').then((m) => ({ default: m.WorkingHoursPage })));
const FieldPage = lazy(() => import('./sections/activity-field.js').then((m) => ({ default: m.FieldPage })));
const TrackingPage = lazy(() => import('./sections/activity-field.js').then((m) => ({ default: m.TrackingPage })));
const WorkTimePage = lazy(() => import('./sections/activity-field.js').then((m) => ({ default: m.WorkTimePage })));
const SalesDefaultsPage = lazy(() => import('./sections/sales-defaults.js').then((m) => ({ default: m.SalesDefaultsPage })));
const DocumentNumberingPage = lazy(() => import('./sections/DocumentNumberingPage.js').then((m) => ({ default: m.DocumentNumberingPage })));
const PricingTemplatesSettings = lazy(() => import('./PricingTemplatesSettings.js').then((m) => ({ default: m.PricingTemplatesSettings })));
const PricingTemplatePage = lazy(() => import('./PricingTemplatePage.js').then((m) => ({ default: m.PricingTemplatePage })));
const ChecklistTemplatesSettings = lazy(() => import('./ChecklistTemplatesSettings.js').then((m) => ({ default: m.ChecklistTemplatesSettings })));
const ChecklistTemplatePage = lazy(() => import('./ChecklistTemplatePage.js').then((m) => ({ default: m.ChecklistTemplatePage })));
const MachineCatalogueSettings = lazy(() => import('./MachineCatalogueSettings.js').then((m) => ({ default: m.MachineCatalogueSettings })));
const CerfaIssuerPage = lazy(() => import('./CerfaIssuerPage.js').then((m) => ({ default: m.CerfaIssuerPage })));
const MachineModelPage = lazy(() => import('./MachineModelPage.js').then((m) => ({ default: m.MachineModelPage })));
const DunningPage = lazy(() => import('./sections/wrapped.js').then((m) => ({ default: m.DunningPage })));
const FeedbackPage = lazy(() => import('./sections/wrapped.js').then((m) => ({ default: m.FeedbackPage })));
const IntegrationsPage = lazy(() => import('./sections/wrapped.js').then((m) => ({ default: m.IntegrationsPage })));
const SubscriptionPage = lazy(() => import('./sections/SubscriptionPage.js').then((m) => ({ default: m.SubscriptionPage })));
const PennylaneExchangesPage = lazy(() => import('./PennylaneSafety.js').then((m) => ({ default: m.PennylaneExchangesPage })));
const PennylaneOnboardingPage = lazy(() => import('./pennylane/PennylaneOnboardingPage.js').then((m) => ({ default: m.PennylaneOnboardingPage })));
const PennylaneExchangePage = lazy(() => import('./PennylaneSafety.js').then((m) => ({ default: m.PennylaneExchangePage })));
const PennylaneLimitsPage = lazy(() => import('./PennylaneSafety.js').then((m) => ({ default: m.PennylaneLimitsPage })));
const GdprRequestsPage = lazy(() => import('./gdpr/GdprRequestsPage.js').then((m) => ({ default: m.GdprRequestsPage })));
const GdprRequestDetailPage = lazy(() => import('./gdpr/GdprRequestDetailPage.js').then((m) => ({ default: m.GdprRequestDetailPage })));
const PennylanePage = lazy(() => import('./pennylane/PennylanePage.js').then((m) => ({ default: m.PennylanePage })));
const PennylaneTokenPage = lazy(() => import('./pennylane/PennylaneTokenPage.js').then((m) => ({ default: m.PennylaneTokenPage })));

/** Une ancienne adresse des paramètres : sa nouvelle page, paramètres gardés. */
function LegacySettingsRedirect(): ReactNode {
  const location = useLocation();
  return <Navigate to={legacySettingsPath(location.pathname, location.search, location.hash)} replace />;
}

const PAGES: Record<string, ReactNode> = {
  identite: <IdentityPage />,
  apparence: <BrandingPage />,
  membres: <MembersPage />,
  roles: <RolesPage />,
  comptable: <AccountantsPage />,
  audit: <AuditLogPage />,
  messagerie: <MessagingSettingsPage />,
  metiers: <TradesPage />,
  horaires: <WorkingHoursPage />,
  terrain: <FieldPage />,
  suivi: <TrackingPage />,
  temps: <WorkTimePage />,
  pieces: <ReservedPartsPage />,
  checklists: <ChecklistTemplatesSettings />,
  clients: <CustomersPage />,
  fluides: <FluidsSettingsPage />,
  devis: <SalesDefaultsPage />,
  baremes: <PricingTemplatesSettings />,
  catalogue: <MachineCatalogueSettings />,
  relances: <DunningPage />,
  avis: <FeedbackPage />,
  integrations: <IntegrationsPage />,
  abonnement: <SubscriptionPage />,
};

const gateOf = (id: string): RouteGate => {
  const section = ENTREPRISE_SECTIONS.find((item) => item.id === id);
  if (!section) throw new Error(`Rubrique inconnue : ${id}`);
  return { anyOf: section.anyOf, ...(section.module ? { module: section.module } : {}) };
};

const users = { anyOf: ['users.manage'] } as const;
const settings = { anyOf: ['settings.manage'] } as const;
/** Pennylane : les deux droits que ses routes serveur exigent. */
const accounting = { anyOf: ['settings.manage'], allOf: ['accounting.manage_integration'] } as const;

export const settingsRoutes: AppRoute[] = [
  { path: '/entreprise', element: <EntreprisePage />, gate: { anyOf: ENTREPRISE_ANY } },
  // Une page par rubrique, gardée par le même droit que sa ligne de l'accueil.
  ...Object.entries(PAGES).map(([id, element]) => ({ path: `/entreprise/${id}`, element, gate: gateOf(id) })),
  { path: '/entreprise/identite/modifier', element: <IdentityEditPage />, gate: { anyOf: ['company.legal_identity'] } },
  { path: '/entreprise/membres/inviter', element: <InviteMemberPage />, gate: users },
  { path: '/entreprise/membres/:userId', element: <MemberPage />, gate: users },
  { path: '/entreprise/comptable/inviter', element: <InviteAccountantPage />, gate: users },
  { path: '/entreprise/comptable/:userId', element: <AccountantPage />, gate: users },
  { path: '/entreprise/comptable/:userId/membre', element: <ConvertAccountantPage />, gate: users },
  { path: '/entreprise/roles/nouveau', element: <RolePage />, gate: users },
  { path: '/entreprise/roles/:roleId', element: <RolePage />, gate: users },
  // Demandes RGPD reçues par le formulaire public (API1-12) : traitées par l'entreprise.
  { path: GDPR_REQUESTS_PATH, element: <GdprRequestsPage />, gate: settings },
  { path: `${GDPR_REQUESTS_PATH}/:requestId`, element: <GdprRequestDetailPage />, gate: settings },
  { path: '/entreprise/devis/numerotation', element: <DocumentNumberingPage />, gate: settings },
  { path: '/entreprise/baremes/nouveau', element: <PricingTemplatePage />, gate: settings },
  { path: '/entreprise/baremes/:templateId', element: <PricingTemplatePage />, gate: settings },
  { path: '/entreprise/checklists/nouvelle', element: <ChecklistTemplatePage />, gate: settings },
  { path: '/entreprise/checklists/:templateId', element: <ChecklistTemplatePage />, gate: settings },
  { path: '/entreprise/fluides/emetteurs/nouvelle', element: <CerfaIssuerPage />, gate: settings },
  { path: '/entreprise/fluides/emetteurs/:issuerId', element: <CerfaIssuerPage />, gate: settings },
  { path: '/entreprise/catalogue/nouveau', element: <MachineModelPage />, gate: settings },
  { path: '/entreprise/catalogue/:modelId', element: <MachineModelPage />, gate: settings },
  { path: '/entreprise/integrations/pennylane', element: <PennylanePage />, gate: accounting },
  { path: '/entreprise/integrations/pennylane/jeton', element: <PennylaneTokenPage />, gate: accounting },
  { path: '/entreprise/integrations/pennylane/garde-fous', element: <PennylaneLimitsPage />, gate: accounting },
  { path: '/entreprise/integrations/pennylane/etat-des-lieux', element: <PennylaneOnboardingPage />, gate: accounting },
  { path: '/entreprise/integrations/pennylane/echanges', element: <PennylaneExchangesPage />, gate: accounting },
  { path: '/entreprise/integrations/pennylane/echanges/:exchangeId', element: <PennylaneExchangePage />, gate: accounting },
  // Seul Pennylane a une page de connexion (routes ci-dessus, plus précises) :
  // toute autre adresse sous les intégrations ramène à leur liste.
  { path: '/entreprise/integrations/:integrationId', element: <Navigate to="/entreprise/integrations" replace />, gate: settings },
  // Anciennes adresses : ouvertes à tous, la page d'arrivée garde son propre droit
  // (d'anciens liens et favoris y pointent encore pour chacun).
  { path: '/parametres', element: <LegacySettingsRedirect /> },
  { path: '/parametres/*', element: <LegacySettingsRedirect /> },
];
